import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faAward } from "@fortawesome/free-solid-svg-icons";
import useDatabase from "../../hooks/useDatabase";
import "./SpecialtySandwiches.css";

// Import default fallback image for sandwiches
import defaultSandwichImg from '../../assets/images/PlaceholderImage.jpg';

// Reusable Specialty Sandwich Card Component with dynamic image resolution and fallback
const SpecialtySandwichCard = ({ sandwich }) => {
  const [imageError, setImageError] = useState(false);

  // Dynamic slug generation based on sandwich name
  const slug = sandwich.name
    .toLowerCase()
    .replace(/^the\s+/g, "") // remove leading "the"
    .trim()
    .replace(/[^a-z0-9]+/g, "-");

  // Custom image URL or default fallback
  const imageUrl = imageError ? defaultSandwichImg : `/images/sandwiches/${slug}.jpg`;

  return (
    <div className="menu-item">
      <div className="sandwich-image-container">
        <img 
          src={imageUrl} 
          alt={sandwich.imageAlt || sandwich.name}
          className="sandwich-image"
          onError={(e) => {
            setImageError(true);
            e.currentTarget.onerror = null; // Prevent infinite error loops
          }}
          loading="lazy"
        />
      </div>
      <div className="menu-item-header">
        <h3>{sandwich.name}</h3>
        <div className="price">{sandwich.price}</div>
      </div>
      <p className="description">{sandwich.description}</p>
      {sandwich.badge && (
        <div className="badge">
          {sandwich.featured ? (
            <FontAwesomeIcon icon={faAward} />
          ) : (
            <FontAwesomeIcon icon={faStar} />
          )}{" "}
          {sandwich.badge}
        </div>
      )}
    </div>
  );
};

const SpecialtySandwiches = () => {
  const [sandwiches, setSandwiches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load sandwiches from database
  const { isLoading: dbLoading, getAllSpecialtySandwiches } = useDatabase();
  
  useEffect(() => {
    const fetchSandwiches = async () => {
      try {
        if (!dbLoading) {
          // Get sandwiches from database
          const specialtySandwiches = getAllSpecialtySandwiches();
          
          // Map default alt tags
          const sandwichesWithImages = specialtySandwiches.map(sandwich => ({
            ...sandwich,
            imageAlt: `${sandwich.name} sandwich`
          }));
          
          setSandwiches(sandwichesWithImages);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error loading specialty sandwiches:", error);
        setIsLoading(false);
      }
    };

    fetchSandwiches();
  }, [dbLoading, getAllSpecialtySandwiches]);

  if (isLoading) {
    return (
      <div className="loading-spinner">
        <FontAwesomeIcon icon={faStar} spin />
        <span>Loading specialty sandwiches...</span>
      </div>
    );
  }

  return (
    <div>      
      <div className="menu-grid">
        {sandwiches.map((sandwich) => (
          <SpecialtySandwichCard key={sandwich.id} sandwich={sandwich} />
        ))}
      </div>
    </div>
  );
};

export default SpecialtySandwiches;

